import { z } from 'zod';
/**
 * Structured conditions for visibility, dependencies and stage entry. Stored as JSON in configuration, so
 * the studio can edit them and the core, web app and validator all evaluate them the same way.
 *
 *   { question: 'Q031', op: 'eq', value: 'yes' }
 *   { fact: 'industry', op: 'in', value: ['manufacturing', 'logistics'] }
 *   { all: [ ... ] } · { any: [ ... ] } · { not: { ... } }
 *
 * Facts are known things about the organisation (industry, country, tenant kind, and later the digital
 * twin). A missing answer or fact makes every comparison false except `not_answered`: unknown is not
 * "not equal".
 */
export const OPERATORS = [
    'eq',
    'ne',
    'in',
    'not_in',
    'gt',
    'gte',
    'lt',
    'lte',
    'between',
    'contains',
    'answered',
    'not_answered',
];
export const Operator = z.enum(OPERATORS);
const NO_VALUE = new Set(['answered', 'not_answered']);
const LIST_VALUE = new Set(['in', 'not_in']);
const Scalar = z.union([z.string(), z.number(), z.boolean()]);
function checkValue(c, ctx) {
    if (NO_VALUE.has(c.op)) {
        if (c.value !== undefined)
            ctx.addIssue({ code: 'custom', message: `${c.op} takes no value`, path: ['value'] });
        return;
    }
    if (c.value === undefined)
        return ctx.addIssue({ code: 'custom', message: `${c.op} needs a value`, path: ['value'] });
    if (LIST_VALUE.has(c.op) && !Array.isArray(c.value))
        ctx.addIssue({ code: 'custom', message: `${c.op} needs a list`, path: ['value'] });
    if (c.op === 'between' && !(Array.isArray(c.value) && c.value.length === 2)) {
        ctx.addIssue({ code: 'custom', message: 'between needs [low, high]', path: ['value'] });
    }
}
const QuestionTest = z
    .strictObject({
    question: z.string().min(1),
    op: Operator,
    value: z.union([Scalar, z.array(Scalar)]).optional(),
})
    .superRefine(checkValue);
const FactTest = z
    .strictObject({
    fact: z.string().min(1),
    op: Operator,
    value: z.union([Scalar, z.array(Scalar)]).optional(),
})
    .superRefine(checkValue);
export const Condition = z.lazy(() => z.union([
    QuestionTest,
    FactTest,
    z.strictObject({ all: z.array(Condition).min(1) }),
    z.strictObject({ any: z.array(Condition).min(1) }),
    z.strictObject({ not: Condition }),
]));
/** Absent, null, an empty list or an empty string count as unanswered. */
export const isAnswered = (v) => v !== undefined && v !== null && !(Array.isArray(v) && v.length === 0) && v !== '';
/** One comparison, with the rules above. Shared with the rules engine. */
export function compare(actual, op, expected) {
    if (op === 'answered')
        return isAnswered(actual);
    if (op === 'not_answered')
        return !isAnswered(actual);
    if (!isAnswered(actual))
        return false;
    switch (op) {
        case 'eq':
            return actual === expected;
        case 'ne':
            return actual !== expected;
        case 'in':
            return expected.includes(actual);
        case 'not_in':
            return !expected.includes(actual);
        case 'contains':
            return Array.isArray(actual)
                ? actual.includes(expected)
                : typeof actual === 'string' && actual.includes(String(expected));
        case 'gt':
        case 'gte':
        case 'lt':
        case 'lte': {
            const a = num(actual), e = num(expected);
            if (a === null || e === null)
                return false;
            return op === 'gt' ? a > e : op === 'gte' ? a >= e : op === 'lt' ? a < e : a <= e;
        }
        case 'between': {
            const a = num(actual), [lo, hi] = expected.map(num);
            return a !== null && lo != null && hi != null && a >= lo && a <= hi;
        }
    }
}
/** Numbers compare as numbers; ISO dates (YYYY-MM-DD) compare as dates; currency values by amount. */
export function num(v) {
    if (typeof v === 'number')
        return v;
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v))
        return Date.parse(v);
    if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v)))
        return Number(v);
    if (v && typeof v === 'object' && 'amount' in v && typeof v.amount === 'number')
        return v.amount;
    return null;
}
export function evaluate(c, ctx) {
    if ('all' in c)
        return c.all.every((x) => evaluate(x, ctx));
    if ('any' in c)
        return c.any.some((x) => evaluate(x, ctx));
    if ('not' in c)
        return !evaluate(c.not, ctx);
    if ('question' in c)
        return compare(ctx.answers[c.question], c.op, c.value);
    return compare(ctx.facts[c.fact], c.op, c.value);
}
/** Every question key a condition reads, for reference checks and cycle detection. */
export function questionRefs(c, out = new Set()) {
    if (!c)
        return out;
    if ('all' in c)
        c.all.forEach((x) => questionRefs(x, out));
    else if ('any' in c)
        c.any.forEach((x) => questionRefs(x, out));
    else if ('not' in c)
        questionRefs(c.not, out);
    else if ('question' in c)
        out.add(c.question);
    return out;
}
/** Every leaf test on a question, with its value, for checking values against the question's options. */
export function questionTests(c, out = []) {
    if (!c)
        return out;
    if ('all' in c)
        c.all.forEach((x) => questionTests(x, out));
    else if ('any' in c)
        c.any.forEach((x) => questionTests(x, out));
    else if ('not' in c)
        questionTests(c.not, out);
    else if ('question' in c)
        out.push(c);
    return out;
}
//# sourceMappingURL=conditions.js.map